import React from "react";
import { Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  dispatchModalSettingsIsOpen,
  dispatchShowLiveBuses,
  dispatchShowScheduledBuses,
  setGrayscaleMap,
  setLoadingAction,
} from "../../redux/actions";
import { ModalContainer } from "../ModalBottom/BottomBarComponent";
import { zoomEnableSwitch, themeProviderCoMB } from "../../config";
import { FooterContent } from "../Layout/Footer";

const P = styled.p`
  margin-bottom: 0.4rem;
`;

const ModalSettingsContainer = styled(ModalContainer)`
  background-color: inherit;
`;

const ModalSettings = ({
  showModalSettings,
  showStopsNames,
  showTooltips,
}: {
  showModalSettings: boolean;
  showStopsNames: () => void;
  showTooltips: boolean;
}): JSX.Element => {
  const dispatch: Dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [zoom, showLiveBuses, showScheduledBuses, grayscaleMap]: [number, boolean, boolean, boolean] = useAppSelector(
    (state: RootState) => [
      state?.zoom?.zoom,
      state?.appSettings?.showLiveBuses,
      state?.appSettings?.showScheduledBuses,
      state?.appSettings?.grayscaleMap,
    ]
  );

  //* Close this modal
  const closeSettingsModal = () => {
    dispatch(dispatchModalSettingsIsOpen(false));
  };

  return (
    <React.Fragment>
      <Modal
        show={showModalSettings}
        fullscreen={"lg-down"}
        onHide={closeSettingsModal}
        centered={true}
        backdrop={false}
        keyboard={true}
      >
        <Modal.Header
          closeButton={true}
          closeLabel={`${t("Close")}`}
          className={themeProviderCoMB ? "settings-modal-comb" : "settings-modal-class"}
          closeVariant="white"
        >
          <Modal.Title>{t("Settings")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ModalSettingsContainer>
            <Form>
              <OverlayTrigger
                placement="auto"
                overlay={<Tooltip id="real-time-vehicles-tooltip">{t("Show_Real-time_Vehicles")}</Tooltip>}
              >
                <Form.Check
                  className={themeProviderCoMB ? "comb-switch" : ""}
                  type="switch"
                  id="real-time-vehicles-switch"
                  label={t("Show_Real-time_Vehicles")}
                  checked={showLiveBuses}
                  onChange={() => dispatch(dispatchShowLiveBuses(!showLiveBuses))}
                />
              </OverlayTrigger>

              <OverlayTrigger
                placement="auto"
                overlay={<Tooltip id="scheduled-vehicles-tooltip">{t("Show_Scheduled_Vehicles")}</Tooltip>}
              >
                <Form.Check
                  className={themeProviderCoMB ? "comb-switch" : ""}
                  type="switch"
                  id="scheduled-vehicles-switch"
                  label={t("Show_Scheduled_Vehicles")}
                  checked={showScheduledBuses}
                  onChange={() => dispatch(dispatchShowScheduledBuses(!showScheduledBuses))}
                />
              </OverlayTrigger>

              <br />

              <OverlayTrigger placement="auto" overlay={<Tooltip id="grayscale-map-tooltip">{t("Grayscale_Map")}</Tooltip>}>
                <Form.Check
                  className={themeProviderCoMB ? "comb-switch" : ""}
                  type="switch"
                  id="grayscale-map-switch"
                  label={t("Grayscale_Map")}
                  checked={grayscaleMap}
                  onChange={async () => {
                    await dispatch(setLoadingAction(true));
                    await dispatch(setGrayscaleMap(!grayscaleMap));
                  }}
                />
              </OverlayTrigger>

              <br />
              <P>{t("Change_zoom")}</P>

              <OverlayTrigger
                placement="auto"
                overlay={<Tooltip id="show-stops-names-switch">{t("Show_Names_of_Stops")}, Disabled</Tooltip>}
              >
                <span>
                  <Form.Check
                    className={themeProviderCoMB ? "comb-switch" : ""}
                    type="switch"
                    id="show-stops-names-switch"
                    label={t("Show_Names_of_Stops")}
                    onChange={showStopsNames}
                    checked={showTooltips}
                    // disabled={zoom && zoom >= zoomEnableSwitch ? false : true}
                    //^ Tooltips are disabled
                    disabled={zoom && zoom >= zoomEnableSwitch ? true : true}
                  />
                </span>
              </OverlayTrigger>
            </Form>
          </ModalSettingsContainer>
        </Modal.Body>

        <Modal.Footer className={themeProviderCoMB ? "settings-modal-comb" : "settings-modal-class"}>
          <div className="mx-auto" style={{ transform: "translateX(25px)" }}>
            <FooterContent />
          </div>
          <Button
            disabled={false}
            variant={themeProviderCoMB ? "info" : "primary"}
            onClick={closeSettingsModal}
            style={{ width: "50px" }}
          >
            {t("Ok")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default ModalSettings;
